import { useEffect, useState } from "react";
import { ExternalLink, ShoppingBag, BookHeart } from "lucide-react";
import { fetchRecommendations, RECO_GENRES, recoGenreLabel } from "../lib/api";

export default function LecturesRecommandees() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchRecommendations(filter === "all" ? {} : { genre: filter })
      .then((data) => {
        setItems(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [filter]);

  return (
    <div data-testid="page-lectures-recommandees" className="pt-32 pb-20 px-6 md:px-12 lg:px-24 min-h-screen">
      <header className="mb-16 max-w-4xl">
        <p className="text-xs tracking-[0.3em] uppercase text-[#9E4735] font-body mb-6">
          Notre sélection partenaire
        </p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#1A1A1A] leading-[1.05] tracking-tight font-light mb-8">
          Lectures <em className="text-[#2C402E]">recommandées</em>
        </h1>
        <p className="text-lg text-[#4A4A4A] font-body leading-relaxed max-w-2xl mb-6">
          Une sélection de livres que nous aimons, disponibles chez notre librairie partenaire <strong className="text-[#1A1A1A]">7switch</strong>. En achetant via nos liens, vous soutenez directement le travail éditorial de CLED.
        </p>
        <div data-testid="affiliate-banner" className="border-l-2 border-[#9E4735] pl-5 py-3 bg-[#F4F1EA] max-w-3xl">
          <p className="text-sm font-body text-[#4A4A4A] leading-relaxed">
            <span className="font-medium text-[#1A1A1A]"><BookHeart size={14} className="inline mr-1 mb-0.5" />Comment ça marche ?</span> CLED touche une petite commission d'affiliation lorsque vous achetez l'un de ces livres en cliquant sur le bouton "Acheter sur 7switch". Pour vous, le prix reste identique — c'est une manière simple et gratuite de soutenir notre maison d'édition.
          </p>
        </div>
      </header>

      {/* Filters */}
      <div
        data-testid="reco-genre-filters"
        className="flex flex-wrap gap-2 md:gap-4 mb-16 pb-6 border-b border-[#D1CFC7]"
      >
        {RECO_GENRES.map((g) => (
          <button
            key={g.value}
            onClick={() => setFilter(g.value)}
            data-testid={`reco-filter-${g.value}`}
            className={`px-4 md:px-6 py-2 text-sm font-body tracking-wide border transition-all ${
              filter === g.value
                ? "bg-[#2C402E] text-[#FAF9F6] border-[#2C402E]"
                : "bg-transparent text-[#4A4A4A] border-[#D1CFC7] hover:border-[#2C402E] hover:text-[#2C402E]"
            }`}
          >
            {g.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-[#757575] font-body" data-testid="loading-reco">Chargement…</p>
      ) : items.length === 0 ? (
        <div data-testid="no-reco" className="py-20 text-center max-w-xl mx-auto">
          <BookHeart size={48} className="text-[#9E4735] mx-auto mb-6 opacity-60" />
          <p className="font-serif text-2xl text-[#1A1A1A] mb-3">Bientôt disponible</p>
          <p className="text-[#757575] font-body leading-relaxed">
            Nous préparons une sélection éditoriale de livres à vous recommander. Revenez très bientôt pour découvrir nos coups de cœur.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {items.map((r, i) => (
            <RecommendationCard key={r.id} reco={r} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function RecommendationCard({ reco, index }) {
  return (
    <article
      data-testid={`reco-card-${reco.id}`}
      className="group flex flex-col bg-[#FAF9F6] border border-[#D1CFC7] hover:border-[#2C402E] transition-all"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="bg-[#F4F1EA] aspect-[3/4] flex items-center justify-center p-6 overflow-hidden">
        <img
          src={reco.cover_url}
          alt={reco.title}
          className="w-full h-full object-contain shadow-xl transition-transform duration-500 group-hover:scale-[1.03]"
          loading="lazy"
        />
      </div>
      <div className="p-6 flex flex-col flex-1">
        <p className="text-[10px] tracking-[0.25em] uppercase font-body text-[#9E4735] mb-2">
          {recoGenreLabel(reco.genre)}
        </p>
        <h3 className="font-serif text-2xl text-[#1A1A1A] leading-tight font-light mb-2">
          {reco.title}
        </h3>
        <p className="text-sm font-body text-[#4A4A4A] mb-4">
          par <em>{reco.author}</em>
        </p>
        <p className="text-sm font-body text-[#4A4A4A] leading-relaxed mb-6 flex-1">
          {reco.description}
        </p>
        <div className="flex items-end justify-between gap-4 pt-4 border-t border-[#D1CFC7] mb-4">
          {reco.price ? (
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#9E4735]">Prix</p>
              <p className="font-mono text-lg text-[#1A1A1A]">{reco.price.toFixed(2)} €</p>
            </div>
          ) : <span />}
        </div>
        <a
          href={reco.affiliate_url}
          target="_blank"
          rel="noreferrer sponsored"
          data-testid={`reco-buy-${reco.id}`}
          className="inline-flex items-center justify-center gap-2 bg-[#C75D2C] text-[#FAF9F6] hover:bg-[#A84A20] px-6 py-3 font-body tracking-wide transition-all"
        >
          <ShoppingBag size={16} />
          <span>Acheter sur</span>
          <img src="/7switch-logo-white.png" alt="7switch" className="h-4" />
          <ExternalLink size={14} />
        </a>
      </div>
    </article>
  );
}
